import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import useLenis from "./hooks/useLenis";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import LiveChat from "./components/LiveChat";
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import Industries from "./pages/Industries";
import Technology from "./pages/Technology";
import Careers from "./pages/Careers";
import Calculator from "./pages/Calculator";
import Proposal from "./pages/Proposal";
import Residential from "./pages/Residential";
import Commercial from "./pages/Commercial";
import Contact from "./pages/Contact";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import ClientPortal from "./pages/ClientPortal";
import AuthCallback from "./components/AuthCallback";
import PaymentReturn from "./pages/PaymentReturn";
import "./App.css";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Portal() {
  const { hash } = useLocation();
  const sid = new URLSearchParams((hash || "").replace(/^#/, "")).get("session_id");
  return sid ? <AuthCallback /> : <ClientPortal />;
}

function ProtectedRoute({ children }) {
  const { token, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-navy">Loading…</div>;
  return token ? children : <Navigate to="/admin/login" replace />;
}

function PublicLayout({ children }) {
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
      <LiveChat />
    </>
  );
}

function App() {
  useLenis();
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster position="bottom-right" theme="dark" richColors />
        <Routes>
          <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
          <Route path="/residential" element={<PublicLayout><Residential /></PublicLayout>} />
          <Route path="/commercial" element={<PublicLayout><Commercial /></PublicLayout>} />
          <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
          <Route path="/services" element={<PublicLayout><Services /></PublicLayout>} />
          <Route path="/industries" element={<PublicLayout><Industries /></PublicLayout>} />
          <Route path="/technology" element={<PublicLayout><Technology /></PublicLayout>} />
          <Route path="/careers" element={<PublicLayout><Careers /></PublicLayout>} />
          <Route path="/quote" element={<PublicLayout><Calculator /></PublicLayout>} />
          <Route path="/proposal/:token" element={<Proposal />} />
          <Route path="/enterprise-quote" element={<Navigate to="/commercial" replace />} />
          <Route path="/ai-estimate" element={<Navigate to="/quote" replace />} />
          <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/portal" element={<Portal />} />
          <Route path="/payment/success" element={<PaymentReturn mode="success" />} />
          <Route path="/payment/cancel" element={<PaymentReturn mode="cancel" />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
